// Emergent traits — named effects unlocked when a ball's genome matches a
// recipe (a predicate over the genome). Evaluated at birth and cached on the
// entity; not re-evaluated per tick.
//
// Recipes are intentionally varied:
//   - some target a single high/low allele
//   - some target combinations (this trait high AND that trait low)
//   - some use the hidden genes (indices >= BASE_TRAITS.length)
//
// Specializations are encouraged to be MUTUALLY EXCLUSIVE so the player has
// real choices to make (you can't be both Berserker and Tank, etc).

import type { Genome } from '../genome';
import { ALLELE_MAX } from '../genome';
import { BASE_TRAITS, traitIndex } from './base';

/** Effect produced by an emergent trait. The combat / breeding systems
 *  interpret these flags; values are looked up per-tick where relevant. */
export interface EmergentEffect {
  /** Bonus damage % below a HP threshold. */
  rageBonus?: { hpThreshold: number; damageMul: number };
  /** Aura: nearby allies get a damage multiplier. */
  packLeader?: { radius: number; damageMul: number };
  /** Projectiles pierce through N targets. */
  pierce?: number;
  /** On breed, produce +N extra offspring. */
  extraOffspring?: number;
  /** Pickups within radius drift toward this ball. */
  shepherd?: { radius: number; force: number };
  /** Flat damage reduction after armor resolution. */
  ironHide?: { reduction: number };
  /** % chance on hit to slow target. */
  freeze?: { chance: number; duration: number };
  /** Returns to combat after death once per run. */
  phoenix?: boolean;
  /** Periodic burst damage in a radius. */
  pulse?: { radius: number; damage: number; intervalSec: number };
  /** Speeds up maturation of nearby children. */
  nursemaid?: { radius: number; rateMul: number };
}

export interface EmergentTrait {
  id: string;
  label: string;
  description: string;
  /** Effects merged into the entity on birth. */
  effect: EmergentEffect;
  /** Predicate over the genome. */
  matches: (genome: Genome) => boolean;
}

// Helpers for readable recipes.
const HIGH = Math.ceil(ALLELE_MAX * 0.75); // 12
const VERY_HIGH = Math.ceil(ALLELE_MAX * 0.9); // 14
const LOW = Math.floor(ALLELE_MAX * 0.25); // 3
const VERY_LOW = Math.floor(ALLELE_MAX * 0.1); // 1
const MID_LO = Math.floor(ALLELE_MAX * 0.4); // 6
const MID_HI = Math.ceil(ALLELE_MAX * 0.6); // 9

function alleleAt(g: Genome, traitId: string): number {
  const i = traitIndex(traitId);
  if (i < 0) return 0;
  return g[i] ?? 0;
}

function hiddenAt(g: Genome, hiddenIndex: number): number {
  return g[BASE_TRAITS.length + hiddenIndex] ?? 0;
}

export const EMERGENT_TRAITS: readonly EmergentTrait[] = [
  {
    id: 'berserker',
    label: 'Berserker',
    description: 'Wounded balls hit harder.',
    effect: { rageBonus: { hpThreshold: 0.25, damageMul: 1.5 } },
    matches: (g) =>
      alleleAt(g, 'attackDamage') >= HIGH &&
      alleleAt(g, 'maxHealth') <= LOW &&
      alleleAt(g, 'speed') >= HIGH,
  },
  {
    id: 'pack-leader',
    label: 'Pack Leader',
    description: 'Allies in range deal +10% damage.',
    effect: { packLeader: { radius: 120, damageMul: 1.1 } },
    matches: (g) =>
      alleleAt(g, 'cohesion') === ALLELE_MAX &&
      alleleAt(g, 'size') >= MID_LO &&
      alleleAt(g, 'size') <= MID_HI,
  },
  {
    id: 'glass-cannon',
    label: 'Glass Cannon',
    description: 'Projectiles pierce up to 3 targets.',
    effect: { pierce: 3 },
    matches: (g) =>
      alleleAt(g, 'attackDamage') === ALLELE_MAX &&
      alleleAt(g, 'armor') === 0 &&
      alleleAt(g, 'maxHealth') === 0,
  },
  {
    id: 'elder',
    label: 'Elder',
    description: 'Produces an extra offspring when bred.',
    effect: { extraOffspring: 1 },
    matches: (g) =>
      alleleAt(g, 'lifespan') >= VERY_HIGH && alleleAt(g, 'fertilityWindowEnd') >= MID_HI,
  },
  {
    id: 'shepherd',
    label: 'Shepherd',
    description: 'Pulls nearby breeding pickups toward itself.',
    effect: { shepherd: { radius: 200, force: 80 } },
    matches: (g) =>
      alleleAt(g, 'pickupAttraction') >= VERY_HIGH &&
      alleleAt(g, 'speed') >= MID_LO &&
      alleleAt(g, 'speed') <= MID_HI,
  },
  {
    id: 'iron-hide',
    label: 'Iron Hide',
    description: 'Reduces incoming damage by a flat amount.',
    effect: { ironHide: { reduction: 1 } },
    matches: (g) =>
      alleleAt(g, 'armor') >= HIGH && alleleAt(g, 'mass') >= HIGH && alleleAt(g, 'speed') <= LOW,
  },
  {
    id: 'frostbitten',
    label: 'Frostbitten',
    description: 'Attacks have a chance to slow enemies.',
    effect: { freeze: { chance: 0.2, duration: 1.2 } },
    matches: (g) =>
      alleleAt(g, 'attackRate') >= HIGH && alleleAt(g, 'crit') >= MID_LO && hiddenAt(g, 0) >= HIGH,
  },
  {
    id: 'phoenix',
    label: 'Phoenix',
    description: 'Returns once after death.',
    effect: { phoenix: true },
    matches: (g) =>
      alleleAt(g, 'regen') >= HIGH && alleleAt(g, 'lifespan') <= LOW && hiddenAt(g, 1) >= VERY_HIGH,
  },
  {
    id: 'pulsar',
    label: 'Pulsar',
    description: 'Emits periodic shockwaves.',
    effect: { pulse: { radius: 90, damage: 2, intervalSec: 2 } },
    matches: (g) =>
      alleleAt(g, 'attackDamage') >= MID_HI &&
      alleleAt(g, 'attackRate') <= LOW &&
      hiddenAt(g, 2) >= HIGH,
  },
  {
    id: 'nursemaid',
    label: 'Nursemaid',
    description: "Speeds up nearby young balls' maturation.",
    effect: { nursemaid: { radius: 100, rateMul: 1.5 } },
    matches: (g) =>
      alleleAt(g, 'maturationRate') >= HIGH &&
      alleleAt(g, 'cohesion') >= MID_HI &&
      alleleAt(g, 'attackDamage') <= VERY_LOW,
  },
];

/** Evaluate which emergent traits a genome unlocks. */
export function evaluateEmergent(genome: Genome): EmergentTrait[] {
  const matched: EmergentTrait[] = [];
  for (const e of EMERGENT_TRAITS) {
    if (e.matches(genome)) matched.push(e);
  }
  return matched;
}

/** Merge multiple effects from all matched emergent traits into one. */
export function mergeEffects(traits: readonly EmergentTrait[]): EmergentEffect {
  const out: EmergentEffect = {};
  for (const t of traits) {
    Object.assign(out, t.effect);
  }
  return out;
}
